import { useEffect, useRef } from "react";
import { HiddenPlayerWrapper, WarningText } from "./EmbedPlayerStyles";

declare global {
  interface Window {
    SC: any;
  }
}

interface EmbedPlayerProps {
  url: string;
  isPlaying: boolean;
}

const parseUrl = (rawUrl: string): URL | null => {
  if (!rawUrl || rawUrl.trim().length < 4) return null;

  try {
    return new URL(rawUrl);
  } catch {
    try {
      return new URL(`https://${rawUrl}`);
    } catch {
      return null;
    }
  }
};

const EmbedPlayer: React.FC<EmbedPlayerProps> = ({ url, isPlaying }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const widgetRef = useRef<any>(null);

  const parsedUrl = parseUrl(url);
  const hostname = parsedUrl?.hostname.toLowerCase() ?? "";

  const isYouTube =
    hostname.includes("youtube.com") || hostname.includes("youtu.be");
  const isSoundCloud = hostname.includes("soundcloud.com");
  const isSpotify = hostname.includes("spotify.com");

  const getEmbedUrl = (): string | null => {
    if (!parsedUrl) return null;

    if (isYouTube) {
      const videoId =
        parsedUrl.searchParams.get("v") || parsedUrl.pathname.split("/").pop();
      if (!videoId || videoId.length < 5) return null;

      return `https://www.youtube.com/embed/${videoId}?autoplay=${
        isPlaying ? 1 : 0
      }&controls=0&mute=0&enablejsapi=1`;
    }

    if (isSoundCloud) {
      return `https://w.soundcloud.com/player/?url=${encodeURIComponent(
        parsedUrl.href
      )}&auto_play=false&visual=false&show_artwork=false`;
    }

    if (isSpotify) {
      const parts = parsedUrl.pathname.split("/").filter(Boolean);
      if (parts.length === 2) {
        return `https://open.spotify.com/embed/${parts[0]}/${parts[1]}?utm_source=generator`;
      }
    }

    return null;
  };

  const embedUrl = getEmbedUrl();

  useEffect(() => {
    if (!isSoundCloud || !iframeRef.current || !window.SC) return;

    const widget = window.SC.Widget(iframeRef.current);
    widgetRef.current = widget;

    widget.bind(window.SC.Widget.Events.READY, () => {
      if (isPlaying) {
        widget.play();
      } else {
        widget.pause();
      }
    });

    return () => {
      widget.unbind(window.SC.Widget.Events.READY);
    };
  }, [url, isPlaying, isSoundCloud]);

  const renderWarning = () => {
    const knownSources = ["youtube", "soundcloud", "spotify"];
    const maybeKnown = knownSources.some((p) => url.toLowerCase().includes(p));

    if (url && !embedUrl && maybeKnown) {
      return (
        <WarningText>
          ⚠️ O link parece ser do YouTube, SoundCloud ou Spotify, mas está mal
          formatado.
        </WarningText>
      );
    }

    return null;
  };

  if (!embedUrl) return renderWarning();

  return (
    <HiddenPlayerWrapper>
      <iframe
        ref={iframeRef}
        title="hidden-player"
        src={embedUrl}
        width="100%"
        height="1"
        allow="autoplay"
        allowFullScreen
        frameBorder="0"
      />
    </HiddenPlayerWrapper>
  );
};

export default EmbedPlayer;
